import { storage } from "@/lib/storage";

const TZ = "Europe/Prague";

export function formatDate(value) {
  if (!value) return "—";
  const d = typeof value === "string" ? new Date(value) : value;
  if (isNaN(d)) return "—";
  return new Intl.DateTimeFormat("cs-CZ", { day: "numeric", month: "numeric", year: "numeric", timeZone: TZ }).format(d);
}

// Krátké datum do tabulek (224): letos „12. 12.“, jiný rok „12. 12. 25“.
// Plné datum patří do `title`, ať jde vždy dohledat.
export function formatDateShort(value, now = new Date()) {
  if (!value) return "—";
  const d = typeof value === "string" ? new Date(value) : value;
  if (isNaN(d)) return "—";
  const parts = new Intl.DateTimeFormat("cs-CZ", { day: "numeric", month: "numeric", year: "numeric", timeZone: TZ }).formatToParts(d);
  const get = (t) => (parts.find((p) => p.type === t) || {}).value;
  const thisYear = new Intl.DateTimeFormat("cs-CZ", { year: "numeric", timeZone: TZ }).format(now);
  return `${get("day")}. ${get("month")}.${get("year") === thisYear ? "" : ` ${String(get("year")).slice(-2)}`}`;
}

export function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d)) return "—";
  return new Intl.DateTimeFormat("cs-CZ", {
    day: "numeric", month: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: TZ,
  }).format(d);
}

export function formatTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d)) return "—";
  return new Intl.DateTimeFormat("cs-CZ", { hour: "2-digit", minute: "2-digit", timeZone: TZ }).format(d);
}

// --- Měna (per firma: CZK nebo EUR) ---
// Firma jede v jedné měně. Aktivní měnu nastaví AuthContext po načtení firmy
// (setActiveCurrency). Historicky pojmenované formatCZK/formatCZKCompact proto
// formátují v AKTIVNÍ měně firmy — díky tomu se ~22 volajících nemuselo měnit.
const CURRENCY_META = {
  CZK: { locale: "cs-CZ", code: "CZK", mil: "mil. Kč" },
  EUR: { locale: "sk-SK", code: "EUR", mil: "mil. €" },
};
const normCur = (c) => (String(c).toUpperCase() === "EUR" ? "EUR" : "CZK");
// 401: doklad v jiné cizí měně (USD, PLN…) — její kód, ne „Kč“; firma sama jede jen v CZK nebo EUR
const metaOf = (c) => {
  const u = String(c || ACTIVE_CURRENCY).toUpperCase();
  return CURRENCY_META[u] || (/^[A-Z]{3}$/.test(u) ? { locale: "cs-CZ", code: u, mil: `mil. ${u}` } : CURRENCY_META[normCur(u)]);
};

let ACTIVE_CURRENCY = storage.get("pp-currency") || "CZK";
ACTIVE_CURRENCY = normCur(ACTIVE_CURRENCY);

export function setActiveCurrency(cur) {
  ACTIVE_CURRENCY = normCur(cur);
  storage.set("pp-currency", ACTIVE_CURRENCY);
}
export function activeCurrency() { return ACTIVE_CURRENCY; }
// Symbol aktivní měny firmy (pro popisky inputů: „Sazba (Kč/h)" apod.).
export function currencySymbol(currency) {
  const code = metaOf(currency).code;
  return code === "EUR" ? "€" : code === "CZK" ? "Kč" : code;
}

// Formát částky v dané měně (nebo v aktivní měně firmy, když currency chybí).
export function formatMoney(value, currency) {
  if (value == null || isNaN(value)) return "—";
  const m = metaOf(currency);
  return new Intl.NumberFormat(m.locale, { style: "currency", currency: m.code, maximumFractionDigits: 0 }).format(value);
}

export function formatMoneyCompact(value, currency) {
  if (value == null || isNaN(value)) return "—";
  const m = metaOf(currency);
  if (Math.abs(value) >= 1_000_000) {
    let s = (value / 1_000_000).toFixed(1).replace(".", ",");
    if (s.endsWith(",0")) s = s.slice(0, -2);
    return `${s} ${m.mil}`;
  }
  return formatMoney(value, currency);
}

// Přesná částka s haléři (229) — detail dokladu a účtenky, kde se kontroluje proti papíru.
export function formatMoneyExact(value, currency) {
  if (value == null || isNaN(value)) return "—";
  const m = metaOf(currency);
  return new Intl.NumberFormat(m.locale, { style: "currency", currency: m.code, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
}

// Zpětně kompatibilní názvy — formátují v aktivní měně firmy.
export const formatCZK = (value) => formatMoney(value);
export const formatCZKCompact = (value) => formatMoneyCompact(value);

// České skloňování číslovek: 1 / 2–4 / 5+. Prostá konkatenace („4 faktur") je chyba.
export const plur = (n, a, b, c) => `${n} ${n === 1 ? a : (n >= 2 && n <= 4 ? b : c)}`;

export function formatHours(h) {
  if (h == null) return "0 h";
  return `${Number(h).toLocaleString("cs-CZ", { maximumFractionDigits: 1 })} h`;
}

// ISO/UTC timestamp -> value for <input type="datetime-local"> in Europe/Prague
// wall-clock time (so editing dialogs show local time, not UTC).
export function toLocalInputValue(value) {
  if (!value) return "";
  const d = typeof value === "string" ? new Date(value) : value;
  if (isNaN(d)) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false, timeZone: TZ,
  }).formatToParts(d).reduce((a, p) => (a[p.type] = p.value, a), {});
  const hh = parts.hour === "24" ? "00" : parts.hour;
  return `${parts.year}-${parts.month}-${parts.day}T${hh}:${parts.minute}`;
}
